import type { SVGProps, FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";

import { containerStyle } from "./IllustrationHead.css";

export type IllustrationHeadProps = SVGProps<SVGSVGElement> & {
  variant?: "gradient" | "white";
  face?: "smile" | "quirky" | "none";
  direction?: "left" | "right";
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<SVGSVGElement>;
};

export const IllustrationHead: FC<IllustrationHeadProps> = ({
  variant = "gradient",
  face = "smile",
  direction = "right",
  className,
  ref,
  ...props
}) => (
  <svg
    ref={ref}
    viewBox="0 0 48 53"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={mergeClasses(containerStyle, className)}
    aria-hidden="true"
    {...props}
  >
    {variant === "gradient" && (
      <defs>
        <linearGradient id="headGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C467D5" />
          <stop offset="50%" stopColor="#FF6B6E" />
          <stop offset="80%" stopColor="#FB9458" />
        </linearGradient>
      </defs>
    )}
    <path
      d="M47.9075 30.6543C47.8742 20.2515 47.3014 20.2671 47.3014 20.2671C47.3014 17.547 46.0377 14.9892 43.9011 13.3871C43.9011 13.3871 44.3678 13.1779 37.3883 7.95933L37.358 7.93747C36.3488 7.18482 35.0274 7.03804 33.8879 7.55958L29.0389 9.77068C28.5843 9.9768 28.157 9.42715 28.4571 9.01803L31.721 4.51464C31.8877 4.28354 31.8332 3.95875 31.5998 3.79947H31.5937C31.4574 3.70266 31.2816 3.68704 31.1331 3.75575L24.4991 6.77883C24.0445 6.98495 23.6172 6.4353 23.9172 6.02618L26.0811 3.0437C26.769 2.09743 26.2114 0.729546 25.0719 0.582764C23.0262 0.317308 20.9078 0.838851 19.1591 2.15052C19.1591 2.15052 16.5831 3.29667 10.2279 8.31223C3.86968 13.3278 4.18184 13.3903 4.18184 13.3903C2.04525 14.9955 0.781488 17.5501 0.781488 20.2703C0.781488 20.2703 0.145059 20.4889 0.175365 29.8298C0.205672 39.1708 0.781488 42.4624 0.781488 42.4624C0.781488 47.1688 4.49096 50.982 9.06416 50.982C9.06416 50.982 9.643 52.2218 23.8687 52.2312C38.0944 52.2406 39.0218 50.982 39.0218 50.982C43.598 50.982 47.3045 47.1688 47.3045 42.4624C47.3045 42.4624 47.9439 41.0602 47.9106 30.6574L47.9075 30.6543Z"
      fill={variant === "gradient" ? "url(#headGradient)" : "white"}
    />
    {face === "quirky" && (
      <g
        transform={
          direction === "right" ? "scale(-1, 1) translate(-48, 0)" : undefined
        }
      >
        <path
          d="M15.2953 32.0572C16.245 32.0572 17.0149 31.0257 17.0149 29.7532C17.0149 28.4807 16.245 27.4492 15.2953 27.4492C14.3456 27.4492 13.5757 28.4807 13.5757 29.7532C13.5757 31.0257 14.3456 32.0572 15.2953 32.0572Z"
          fill="#000"
        />
        <path
          d="M30.6503 32.0572C31.6 32.0572 32.3699 31.0257 32.3699 29.7532C32.3699 28.4807 31.6 27.4492 30.6503 27.4492C29.7006 27.4492 28.9307 28.4807 28.9307 29.7532C28.9307 31.0257 29.7006 32.0572 30.6503 32.0572Z"
          fill="#000"
        />
        <path
          d="M22.5156 36.4729C21.4817 36.4729 20.7548 36.199 20.345 35.651C19.9627 35.1405 19.9799 34.4939 20.1925 34.1196C21.0857 32.5473 22.5138 28.1469 22.5293 28.1027C22.6304 27.7896 22.9682 27.6177 23.2836 27.7181C23.5991 27.8185 23.7723 28.1537 23.6711 28.4668C23.6111 28.6523 22.2001 32.9965 21.2469 34.6879C21.24 34.7168 21.2194 34.8479 21.3326 34.9721C21.4629 35.115 22.0561 35.5455 24.2386 35.0606C24.5626 34.9891 24.8832 35.1916 24.9552 35.5115C25.0273 35.8331 24.8232 36.1513 24.5009 36.2228C23.7465 36.3895 23.0831 36.4729 22.5138 36.4729H22.5156ZM21.2417 34.6981C21.2417 34.6981 21.24 34.7015 21.2383 34.7032C21.2383 34.7032 21.24 34.6998 21.2417 34.6981Z"
          fill="#000"
        />
        <path
          d="M11.2774 23.4704C11.1042 23.4704 10.9311 23.3955 10.8128 23.2526C10.6036 22.999 10.6396 22.6247 10.8968 22.4154C10.9345 22.3847 14.6549 19.4188 18.5793 22.1652C18.8502 22.3541 18.9153 22.7268 18.7233 22.9956C18.533 23.2645 18.1575 23.3291 17.8867 23.1386C14.72 20.9231 11.7797 23.2373 11.6563 23.3376C11.5448 23.4278 11.4111 23.4704 11.2774 23.4704Z"
          fill="#000"
        />
        <path
          d="M24.3562 43.2729C23.6327 43.2729 22.8457 43.1384 22.0022 42.8083C21.6936 42.6875 21.5427 42.3421 21.6645 42.0358C21.7862 41.7295 22.1342 41.5797 22.4428 41.7006C26.0484 43.1112 28.3526 40.1725 28.4503 40.0483C28.6509 39.7862 29.0264 39.7352 29.2904 39.9326C29.5544 40.13 29.6076 40.5026 29.4087 40.7647C29.3847 40.7953 27.4662 43.2729 24.3562 43.2729Z"
          fill="#000"
        />
        <path
          d="M27.1704 23.4704C26.9973 23.4704 26.8241 23.3955 26.7058 23.2526C26.4967 22.999 26.5327 22.6247 26.7898 22.4154C26.8276 22.3847 30.548 19.4188 34.4724 22.1652C34.7433 22.3541 34.8084 22.7268 34.6164 22.9956C34.4261 23.2645 34.0506 23.3291 33.7797 23.1386C30.6131 20.9214 27.6728 23.2356 27.5493 23.3359C27.4379 23.4261 27.3042 23.4687 27.1704 23.4687V23.4704Z"
          fill="#000"
        />
      </g>
    )}

    {face === "smile" && (
      <g
        transform={
          direction === "left"
            ? "scale(-1, 1) translate(-48, 0) matrix(0.35852611,0,0,0.35852611,4.6178723,33.350833)"
            : "matrix(0.35852611,0,0,0.35852611,4.6178723,33.350833)"
        }
      >
        <path
          fill="#000"
          d="m 68.76833,-33.65807 a 1.1362902,0.95149559 87.3 0 1 -0.84534,-0.92004 c -0.24377,-1.40365 0.78243,-2.35908 2.09958,-2.72867 3.76666,-1.05372 9.89634,-1.39972 12.22397,2.63431 a 1.4311752,1.1126994 51.3 0 1 0.0196,1.42331 c -0.72738,0.96722 -1.75751,0.73525 -2.64217,-0.0629 -1.0026,-0.90824 -1.93051,-1.60417 -3.29484,-1.75751 q -3.29092,-0.36173 -6.26336,1.21099 -0.55832,0.29882 -1.29749,0.20052 z"
        />
        <path
          fill="#000"
          d="m 43.40428,-36.2216 q 0.64875,0.92397 -0.14547,2.08385 -1.62384,-0.0472 -3.31844,-0.29095 -2.53994,-0.36566 -3.29485,-0.31061 c -2.8191,0.20445 -5.27254,1.26604 -7.7024,2.70901 -0.68413,0.40497 -1.91085,0.55438 -1.94231,-0.54259 -0.0315,-1.11663 0.86107,-2.07599 1.76145,-2.68149 q 2.86235,-1.91478 6.69192,-2.17821 1.65529,-0.1101 5.29221,0.44822 1.82042,0.28309 2.65789,0.76277 z"
          id="path78"
        />
        <path
          fill="#000"
          d="m 75.57427,-19.90856 c 5.09561,0.23591 3.53076,12.44414 -0.57404,12.25542 -4.1048,-0.19266 -4.52157,-12.49526 0.57404,-12.25542 z"
        />
        <path
          fill="#000"
          d="m 41.56027,-16.49183 q 1.77717,4.17164 0.32634,8.31969 c -1.0262,2.0524 -3.07467,2.55567 -4.49405,0.74704 -1.98949,-2.53208 -2.59499,-8.34721 -0.1612,-10.6827 1.68674,-1.6081 3.52682,-0.0275 4.32891,1.61597 z"
        />
        <path
          fill="#000"
          d="M 57.07515,5.60489 A 0.39318,0.39318 0 0 1 57.5391,5.4083 q 3.98292,1.21492 8.09558,0.65268 c 2.68935,-0.36566 2.24113,-2.59499 1.39186,-4.30532 -1.73393,-3.47965 -4.73782,-7.3682 -6.60149,-11.63813 q -0.39712,-0.91611 -0.45216,-2.48883 a 1.9541046,1.9580364 79.5 0 1 1.32108,-1.91872 l 0.38532,-0.12975 a 1.0144044,1.0144044 0 0 1 1.34074,0.96722 q -0.0157,2.96458 1.47836,5.46127 2.68935,4.49012 4.50977,7.09297 2.01702,2.89381 1.68282,5.99206 -0.15728,1.43118 -0.9397,2.25292 c -2.46524,2.59499 -9.40487,2.076 -12.51099,0.51507 A 0.97508639,0.97508639 0 0 1 56.70556,6.92597 q 0.0393,-0.67234 0.36959,-1.32108 z"
        />
        <path
          fill="#000"
          d="m 47.81183,20.29802 6.62508,1.78111 q 6.25157,1.3722 11.92122,-1.17168 c 1.19134,-0.53472 2.00129,-0.88072 2.66576,0.346 a 1.0458588,1.0930404 57.4 0 1 -0.33027,1.3604 c -1.37613,1.01834 -3.25553,1.47836 -4.8715,1.98163 q -3.14937,0.69593 -4.32105,0.75098 -5.33938,0.24377 -10.45465,-1.45477 -3.40101,-1.08125 -6.33807,-3.0904 c -1.20706,-0.82961 -2.91346,-2.81123 -1.44297,-4.05368 a 0.94363199,0.93576839 50.2 0 1 1.32895,0.12582 q 2.0524,2.48096 5.2175,3.42459 z"
        />
      </g>
    )}
  </svg>
);
